import { plainToClass } from 'class-transformer'
import Report from '@/models/report'
import Response from '@/utils/response'
import { storageCache } from '@/utils/storage-cache'
import pageLoading from '@/mixins/page-loading'
import pageTitle from '@/mixins/page-title'
import DialogConfirm from '@/components/dialog/DialogConfirm'

export default {
  components: {
    DialogConfirm
  },
  mixins: [
    pageLoading,
    pageTitle
  ],
  props: {
    repository: Object,
    id: Number,
    report_schedule_id: Number
  },
  data () {
    return {
      report: new Report(),
      reportTheme: null,
      processing: false,
      isResult: false,
      submittable: false,
      confirmDialog: {
        active: false,
        body: null,
        handleClose: null,
        handleAgreee: null
      }
    }
  },
  computed: {
    editMode () {
      return this.$route.path.match(/edit/) !== null
    },
    storageCacheKey () {
      if (this.id) {
        return `reportBodyEdit-${this.id}`
      } else {
        return `reportBodyNew-${this.report_schedule_id}`
      }
    }
  },
  watch: {
    '$route': 'restart',
    report () {
      this.$store.commit('setBackRoute', {
        name: 'reportSchedule-show',
        params: { id: this.report.report_schedule.id }
      })
    },
    'report.body' (value) {
      if (!value) {
        return
      }

      storageCache.write(this.storageCacheKey, value, {expireDays: 7})

      this.$validator.validate().then(result => {
        this.submittable = result
      })
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    onEdit () {
      if (this.editMode)
        this.restart()
      else {
        this.$router.push({
          name: 'report-edit',
          params: { id: this.report.id }
        })
      }
    },
    onClose () {
      window.open('about:blank', '_self').close()
    },
    onReturnTop () {
      this.$router.replace({
        name: 'reportSchedule-show',
        params: { id: this.report.report_schedule.id }
      })
    },
    restart () {
      this.isResult = false
      this.fetchData()
    },
    callRepository () {
      if (this.editMode) {
        return this.repository.edit(this.id)
      } else {
        return this.repository.new(this.report_schedule_id)
      }
    },
    async fetchData () {
      if (this.loading) return
      this.loading = true
      try {
        const cacheValue = storageCache.read(this.storageCacheKey)
        const response = new Response(await this.callRepository())
        this.report = plainToClass(Report, response.result)
        this.reportTheme = this.report.reportTheme

        if (this.editMode || !this.report.isLoaded) {
          if (cacheValue && cacheValue !== this.report.body) {
            this.confirmDialog = {
              active: true,
              body: '編集中のデータがあります、復元しますか？',
              handleClose: () => {
                this.confirmDialog.active = false
              },
              handleAgree: () => {
                this.report.body = cacheValue
                this.confirmDialog.active = false
              }
            }
          }
        } else {
          this.isResult = true
        }
      } catch (error) {
        this.$store.commit('setAxiosError', error)
      } finally {
        this.loading = false
      }
    },
    onAnswer () {
      this.$validator.validate().then(result => {
        if (!result) return
        if (this.processing) return
        this.processing = true
        if (this.editMode)
          this.doUpdate()
        else
          this.doCreate()
      })
    },
    async doCreate () {
      try {
        const response = new Response(
          await this.repository.create(this.report_schedule_id, {
            report: this.report.toParams()
          }))
        this.report = plainToClass(Report, response.result)
        this.isResult = true
        storageCache.delete(this.storageCacheKey)
      } catch (error) {
        this.$store.commit('setAxiosError', error)
      } finally {
        this.processing = false
      }
    },
    async doUpdate () {
      try {
        const response = new Response(
          await this.repository.update(this.id, {
            report: this.report.toParams()
          }))
        this.report = plainToClass(Report, response.result)
        this.isResult = true
        storageCache.delete(this.storageCacheKey)
      } catch (error) {
        this.$store.commit('setAxiosError', error)
      } finally {
        this.processing = false
      }
    }
  }
}
